import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { SocialPost } from '../../types/social';

interface SocialVideoCardProps {
    post: SocialPost;
    aspectRatio?: 'portrait' | 'square';
}

export const SocialVideoCard: React.FC<SocialVideoCardProps> = ({ 
    post, 
    aspectRatio = 'portrait' 
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [isHovered, setIsHovered] = useState(false);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleMute = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    return (
        <motion.div 
            className={`relative group overflow-hidden bg-zinc-900 rounded-sm cursor-pointer mx-auto w-full max-w-[280px] ${
                aspectRatio === 'portrait' ? 'aspect-[9/16]' : 'aspect-square'
            }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onClick={togglePlay}
        >
            {/* Video Element */}
            <video
                ref={videoRef}
                src={post.video_url}
                poster={post.thumbnail_url}
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
                loop
                muted={isMuted}
                playsInline
            />

            {/* Premium Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />

            {/* Play/Pause Center Icon */}
            <AnimatePresence>
                {!isPlaying && (
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                        <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                            <Play className="text-white fill-white ml-1" size={24} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Bottom Info & Controls */}
            <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                {post.caption && (
                    <p className="text-white text-xs font-medium mb-3 line-clamp-2 drop-shadow-lg">
                        {post.caption}
                    </p>
                )}
                
                <div className="flex items-center justify-between">
                    {/* Racing Blue Glow Badge */}
                    <div className="px-2 py-0.5 bg-zinc-950 border border-[#1aaaff]/30 rounded-full">
                        <span className="text-[8px] font-black uppercase tracking-[0.2em] text-[#1aaaff] drop-shadow-[0_0_5px_rgba(26,170,255,0.5)]">
                            Social Proof
                        </span>
                    </div>

                    <button 
                        onClick={toggleMute}
                        className="w-8 h-8 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all"
                    >
                        {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                    </button>
                </div>
            </div>

            {/* Top Border Glow (Racing Blue) */}
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#1aaaff]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </motion.div>
    );
};
